import {
    Input,
    Output,
    OnInit,
    OnDestroy,
    Directive,
    ElementRef,
    EventEmitter,
    HostListener,
} from '@angular/core';
import { ui } from '@qwyk/models';

@Directive({
    standalone: true,
    selector: '[qwykGooglePlacesAutocomplete]',
})
export class GooglePlacesAutocompleteDirective implements OnInit, OnDestroy {
    @Output() placeSelected: EventEmitter<ui.GooglePlacesAddress> =
        new EventEmitter();
    @Output() invalidate: EventEmitter<boolean> = new EventEmitter();
    @Input() placeTypes: string[] = ['address'];
    private listener: google.maps.MapsEventListener;

    constructor(private elRef: ElementRef) {}

    ngOnInit() {
        // const serv = new google.maps.places.AutocompleteService();
        // serv.getPlacePredictions({input: 'Nieuwstraat 7'}, (result) => {
        //     console.log(result);
        // });

        const autocomplete = new google.maps.places.Autocomplete(
            this.elRef.nativeElement,
            {
                types: this.placeTypes,
            }
        );

        this.listener = google.maps.event.addListener(
            autocomplete,
            'place_changed',
            () => {
                const place = autocomplete.getPlace();
                if (Object.keys(place).length < 2) {
                    // If you hit enter in this control it will trigger place_changed but only the `name` prop will be set with the input.
                    // Test for this and return if we have less than 2 props.
                    this.invalidate.emit(true);
                } else {
                    this.placeSelected.emit(
                        this.getFormattedAddress(autocomplete.getPlace())
                    );
                }
            }
        );
    }

    ngOnDestroy() {
        if (this.listener) {
            this.listener.remove();
        }
    }

    @HostListener('keydown.backspace', ['$event'])
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onInputBackspace(e: KeyboardEvent) {
        this.invalidate.emit(true);
    }

    /**
     * Handles clearing of the input by hitting the spacebar
     * @param e
     */
    @HostListener('keydown', ['$event'])
    onKeyDown(e: { target: { value: string } }) {
        if (e?.target?.value?.trim() === '' || !e?.target?.value) {
            this.invalidate.emit(true);
        }
    }

    private getFormattedAddress(
        place: google.maps.places.PlaceResult
    ): ui.GooglePlacesAddress {
        const location: ui.GooglePlacesAddress = {
            formattedAddress: '',
            streetNumber: '',
            route: '',
            locality: '',
            postalCode: '',
            state: '',
            countryName: '',
            countryCode: '',
            position: {
                lat: 0,
                lng: 0,
            },
            types: [],
            code: '',
            placeId: '',
            plusCode: '',
        };
        for (const i in place.address_components) {
            // eslint-disable-next-line no-prototype-builtins
            if (!place.address_components.hasOwnProperty(i)) {
                continue;
            }

            location.formattedAddress =
                this.elRef.nativeElement.value ?? place.formatted_address;
            if (place.types) {
                location.types = place.types;
            }
            if (place.geometry?.location) {
                location.position = {
                    lat: place.geometry.location.lat(),
                    lng: place.geometry.location.lng(),
                };
            }
            if (place.place_id) {
                location.placeId = place.place_id;
            }
            if (place.plus_code?.global_code) {
                location.plusCode = place.plus_code.global_code;
            }

            if (place.address_components) {
                for (const item of place.address_components) {
                    if (item.types.includes('locality')) {
                        if (!location.code) {
                            location.code = item.short_name;
                        }
                        location.locality = item.long_name;
                    } else if (
                        item.types.includes('sublocality') &&
                        !location.locality
                    ) {
                        location.locality = item.long_name;
                    } else if (item.types.includes('administrative_area_level_1')) {
                        location.state = item.short_name;
                    } else if (item.types.includes('street_number')) {
                        location.streetNumber = item.short_name;
                    } else if (item.types.includes('route')) {
                        location.route = item.long_name;
                    } else if (item.types.includes('country')) {
                        location.countryName = item.long_name;
                        location.countryCode = item.short_name;
                    } else if (item.types.includes('postal_code')) {
                        location.code = item.short_name;
                        location.postalCode = item.short_name;
                    }
                }
            }
        }

        try {
            if (location.countryCode === 'CN' && !location.postalCode) {
                const value = this.elRef.nativeElement.value as string;
                const matches = /\d{6}$/gm.exec(value);
                location.postalCode =
                    matches && matches.length > 0 ? matches[0] : '';
            }
        } catch {
            /* empty */
        }

        return location;
    }
}
